import { Box, Typography, TypographyProps } from '@mui/material';
import moment from 'moment';

import { DateRangeDisplay } from '../dates/DateRangeDisplay';
import { AxleChip } from '../displayFields/chips';
import { NoneLabel } from '../labels/NoneLabel';
import { VerticalStack } from '../utils/linearLayouts';

export function InlineOptionalDateRangeDisplay({
    startDate,
    endDate,
    dateProps,
}: {
    startDate: Date | null;
    endDate: Date | null;
    timezone: string;
    captionProps?: TypographyProps;
    dateProps?: TypographyProps;
}) {
    const startDateFormatted = startDate ? moment(startDate).format('ddd, MMM D') : null;
    const endDateFormatted = endDate ? moment(endDate).format('ddd, MMM D') : null;
    return startDateFormatted && endDateFormatted ? (
        <Typography {...dateProps}>
            {startDateFormatted} - {endDateFormatted}
        </Typography>
    ) : startDateFormatted ? (
        <Typography {...dateProps}>After {startDateFormatted}</Typography>
    ) : endDateFormatted ? (
        <Typography {...dateProps}>Before {endDateFormatted}</Typography>
    ) : (
        <AxleChip label={'No fulfillment window'} />
    );
}

function OptionalDateRangeDisplay({
    startDate,
    endDate,
    timezone,
    captionProps,
    dateProps,
}: {
    startDate: Date | null;
    endDate: Date | null;
    timezone: string;
    captionProps?: TypographyProps;
    dateProps?: TypographyProps;
}) {
    return startDate && endDate ? (
        <DateRangeDisplay
            startDate={startDate}
            endDate={endDate}
            timezone={timezone}
            captionProps={captionProps}
            dateProps={dateProps}
        />
    ) : startDate ? (
        <Box>
            <Typography variant='caption' {...captionProps}>
                After
            </Typography>
            <Typography fontWeight='bold' {...dateProps}>
                {moment(startDate).format('dddd MMM D YYYY')}
            </Typography>
        </Box>
    ) : endDate ? (
        <Box>
            <Typography variant='caption' {...captionProps}>
                Before
            </Typography>
            <Typography fontWeight='bold' {...dateProps}>
                {moment(endDate).format('dddd MMM D YYYY')}
            </Typography>
        </Box>
    ) : (
        <AxleChip label={'No fulfillment window'} />
    );
}

export function FulfillmentWindowDisplay({
    fulfillmentWindow: { startDate, endDate },
    timezone,
    captionProps,
    dateProps,
}: {
    fulfillmentWindow: { startDate: Date | null; endDate: Date | null };
    timezone: string;
    captionProps?: TypographyProps;
    dateProps?: TypographyProps;
}) {
    return (
        <VerticalStack>
            <Typography variant='caption' {...captionProps}>
                Fulfillment Window
            </Typography>
            <Box>
                <OptionalDateRangeDisplay
                    startDate={startDate}
                    endDate={endDate}
                    timezone={timezone}
                    captionProps={captionProps}
                    dateProps={dateProps}
                />
            </Box>
        </VerticalStack>
    );
}

export function OptionalFulfillmentWindowDisplay({
    fulfillmentWindow,
    timezone,
    captionProps,
    dateProps,
}: {
    fulfillmentWindow: { startDate: Date | null; endDate: Date | null } | null;
    timezone: string;
    captionProps?: TypographyProps;
    dateProps?: TypographyProps;
}) {
    return fulfillmentWindow ? (
        <FulfillmentWindowDisplay
            fulfillmentWindow={fulfillmentWindow}
            timezone={timezone}
            captionProps={captionProps}
            dateProps={dateProps}
        />
    ) : (
        <VerticalStack>
            <Typography variant='caption' {...captionProps}>
                Fulfillment Window
            </Typography>
            <Box>
                <NoneLabel />
            </Box>
        </VerticalStack>
    );
}
